import React, { useRef, useState, useLayoutEffect } from 'react';
import PointRight from '@/components/icons/point-right';
import PointLeft from '@/components/icons/point-left';

export default function LibraryVerticalScrollbar({
	initialScrollIndex = 0,
	centerItems = false,
	scrollSize = 2,
	children,
}) {
	const scrollableElement = useRef();
	const pointerLeftElement = useRef();

	const [showPointerRight, setShowPointerRight] = useState(false);
	const [showPointerLeft, setShowPointerLeft] = useState(false);
	const [currentScrollIndex, setScrollIndex] = useState(0);
	const [hasSmoothScroll, setSmoothScroll] = useState(false);
	const [canCenterItems, setCanCenterItems] = useState(false);
	const [hasOverflow, setHasOverflow] = useState(true);

	const updateScrollPosition = offset => {
		const el = scrollableElement.current;
		const { children } = el;

		if (children.length > 0) {
			const maxScrollLeft = el.scrollWidth - el.clientWidth;
			const avgChildWidth = el.offsetWidth / children.length;
			const maxScrollIndex = Math.ceil(maxScrollLeft / avgChildWidth);
			const index = Math.max(0, Math.min(maxScrollIndex, currentScrollIndex + offset));
			const newScrollLeft = children[index].offsetLeft - pointerLeftElement.current.offsetWidth;

			setCanCenterItems(maxScrollLeft === 0);
			if (maxScrollLeft > avgChildWidth / 2) {
				setHasOverflow(true);
				setScrollIndex(index);
				setShowPointerRight(newScrollLeft < maxScrollLeft);
				setShowPointerLeft(newScrollLeft > 0);
			} else {
				setHasOverflow(false);
				setShowPointerRight(false);
				setShowPointerLeft(false);
			}

			// When scrolling to the left, remove the addition width of the pointer-element itself
			el.scrollLeft = newScrollLeft;
		}
	};

	const scrollRight = () => {
		updateScrollPosition(1 * scrollSize);
	};

	const scrollLeft = () => {
		updateScrollPosition(-(1 * scrollSize));
	};

	const handleWindowSizeChange = () => {
		updateScrollPosition(0);
	};

	useLayoutEffect(() => {
		const el = scrollableElement.current;
		const { children } = el;

		if (children.length > 0) {
			// Check if we need to scroll if a initialScrollIndex is set
			const maxScrollLeft = el.scrollWidth - el.clientWidth;
			const avgChildWidth = el.offsetWidth / children.length;
			const totalWidthAtIndex = avgChildWidth * (Math.max(0, initialScrollIndex) + 1);
			if (totalWidthAtIndex > el.clientWidth - maxScrollLeft) {
				setScrollIndex(initialScrollIndex);
				updateScrollPosition(initialScrollIndex);
			} else {
				updateScrollPosition(0);
			}
		}

		// We don't want smooth-scroll on inital render
		setSmoothScroll(true);
		window.addEventListener('resize', handleWindowSizeChange);
		return () => {
			window.removeEventListener('resize', handleWindowSizeChange);
		};
	}, []);

	return (
		<div className="w-full relative">
			<div
				className={`absolute z-10 left-0 top-0 bottom-0 bg-white w-11 border-r border-heather flex items-center	 ${
					showPointerLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
				}`}
				ref={pointerLeftElement}
			>
				<button type="button" className="pl-3" onClick={scrollLeft}>
					<PointLeft color="text-heather hover:text-slate-grey active:text-midnight" size="w-6 h-6" />
				</button>
			</div>

			<div
				className={`flex w-full ${centerItems && canCenterItems ? 'justify-center' : ''} ${
					hasOverflow ? 'overflow-x-hidden' : ''
				} ${hasSmoothScroll ? 'scroll-smooth' : ''}`}
				ref={scrollableElement}
			>
				{children}
			</div>

			<div
				className={`absolute z-10 right-0 top-0 bottom-0 bg-white w-11 border-l border-heather flex items-center	 ${
					showPointerRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
				}`}
			>
				<button type="button" className="pl-3" onClick={scrollRight}>
					<PointRight color="text-heather hover:text-slate-grey active:text-midnight" size="w-6 h-6" />
				</button>
			</div>
		</div>
	);
}
